import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { orderBy } from 'lodash';
import React, { useMemo } from 'react';
import VoxelText from '../../../../../components/voxel/Text/VoxelText';
import { MetaRuleFormState } from './formLogic/MetaRuleFormState';

export interface MetaInputProps {
  type: 'textarea' | 'input';
  label: string;
  placeholder: string;
  valueKey: keyof MetaRuleFormState;
  value: string;
  setValue: (value: string) => void;
  disabled: boolean;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    areaSmall: {
      height: '75px',
    },
    areaLarge: {
      height: '100px',
    },
    textarea: {
      ...theme.typography.body2,
      width: '100%',
      padding: theme.spacing(1),
      paddingRight: theme.spacing(2),
      borderRadius: '4px',
      border: 'none',
      borderImage: 'none',
      '&:focus': {
        outline: '1px solid #aaa',
      },
      '&::placeholder': {
        color: theme.palette.text.hint,
      },
      display: 'block',
      position: 'absolute',
      zIndex: 2,
      margin: 0,
      backgroundColor: 'transparent',
      overflow: 'hidden',
      transition: 'transform 1s',
      resize: 'none',
    },
    container: {
      width: '100%',
      display: 'block',
      margin: '0 auto',
      transform: 'translateZ(0)',
      '-webkit-text-size-adjust': 'none',
    },
    backdrop: {
      width: '100%',
      padding: theme.spacing(1),
      paddingRight: theme.spacing(2),
      position: 'absolute',
      zIndex: 1,
      backgroundColor: '#fbfbfb',
      pointerEvents: 'none',
      transition: 'transform 1s',
    },
  })
);

export interface HighlightedText {
  highlightedWord: string;
  startIndex: number;
}

export const MetaInput: React.FC<MetaInputProps> = ({
  type,
  label,
  placeholder,
  value,
  valueKey,
  setValue,
  disabled,
}) => {
  const highlightedValue = useMemo(() => {
    // Step 1: Find highlight positions
    // Example: 'hello [primary keyword] everyone [secondary keyword] dudumdum
    // Becomes: [{ highlightedWord: '[primary keyword]', startIndex: 6 }, { highlightedWord: '[secondary keyword]', startIndex: 32 }]
    const highlightWords = [
      '[primary keyword]',
      '[secondary keyword]',
      '[Primary keyword]',
      '[Secondary keyword]',
    ];
    const highlightedTexts: HighlightedText[] = [];

    highlightWords.forEach((highlightWord) => {
      // Create a function which finds the index of an highlight word
      const getPosition = (position: number) => value?.indexOf(highlightWord, position);

      // Loop through the text to find all positions
      let lastMatchPosition = getPosition(0);
      while (lastMatchPosition > -1) {
        // Insert match position into results
        highlightedTexts.push({
          highlightedWord: highlightWord,
          startIndex: lastMatchPosition,
        });

        // Find next match position
        lastMatchPosition = getPosition(lastMatchPosition + highlightWord.length);
      }
    });

    // Return value as is if there's no highlighted texts
    if (!highlightedTexts.length) {
      return <div className="whitespace-pre-wrap">{value}</div>;
    }

    // Step 2: Create appropriate JSX
    // Order by start index
    const orderedHighlightedTexts = orderBy(highlightedTexts, (t) => t.startIndex);

    return (
      <div>
        {orderedHighlightedTexts.map((highlightedText, index) => {
          const previousHighlightEndIndex =
            (orderedHighlightedTexts[index - 1]?.startIndex ?? 0) +
            (orderedHighlightedTexts[index - 1]?.highlightedWord.length ?? 0);

          const isLastHighlightedText = index + 1 === orderedHighlightedTexts.length;
          return (
            <React.Fragment key={highlightedText.startIndex + highlightedText.highlightedWord}>
              {/* Text before this highlighted text */}
              <span className="whitespace-pre-wrap">
                {value.slice(previousHighlightEndIndex, highlightedText.startIndex)}
              </span>

              {/* Highlighted text */}
              <span className="bg-yellow-200/50">{highlightedText.highlightedWord}</span>

              {/* Text after the very last highlighted text */}
              {isLastHighlightedText && (
                <span className="whitespace-pre-wrap">
                  {value.slice(highlightedText.startIndex + highlightedText.highlightedWord.length)}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }, [value]);

  // Styling
  const classes = useClasses();

  return (
    <div className={'flex flex-col gap-3 justify-end'}>
      <VoxelText variant={'title3'}>
        {label}
      </VoxelText>
      <div>
        <div
          className={clsx({
            [classes.container]: true,
            [classes.areaLarge]: type === 'textarea',
            [classes.areaSmall]: type !== 'textarea',
          })}
        >
          <div
            className={clsx({
              [classes.backdrop]: true,
              [classes.areaLarge]: type === 'textarea',
              [classes.areaSmall]: type !== 'textarea',
            })}
          >
            {highlightedValue}
          </div>
          <textarea
            id={valueKey}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder}
            maxLength={type === 'textarea' ? 320 : 200}
            className={clsx({
              [classes.textarea]: true,
              [classes.areaLarge]: type === 'textarea',
              [classes.areaSmall]: type !== 'textarea',
            })}
            disabled={disabled}
          />
        </div>
      </div>
      <div>
        <VoxelText variant="form-caption" color="medium">
          {value?.length} characters
        </VoxelText>
      </div>
    </div>
  );
};

export default MetaInput;
